import React, { useEffect, useState } from "react";
import { PlusCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { formatDateTime } from "../lib/utils";

const initialForm = {
  name: "",
  address: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  user_id: "",
};

export default function SchoolsPage() {
  const { schools, loadSchools, createSchool, updateSchool } = useData();
  const [form, setForm] = useState(initialForm);
  const [editingSchool, setEditingSchool] = useState(null);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  const resetForm = () => {
    setEditingSchool(null);
    setForm(initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = { ...form, user_id: form.user_id || null };
    const result = editingSchool ? await updateSchool(editingSchool.id, payload) : await createSchool(payload);
    if (result.success) {
      toast.success(editingSchool ? "School updated." : "School created.");
      resetForm();
      loadSchools();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = (school) => {
    setEditingSchool(school);
    setForm({
      name: school.name || "",
      address: school.address || "",
      contact_name: school.contact_name || "",
      contact_phone: school.contact_phone || "",
      contact_email: school.contact_email || "",
      user_id: school.user_id || "",
    });
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr] animate-fade-in">
      <form onSubmit={handleSubmit} className="guardian-card bg-white p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">{editingSchool ? "Edit school" : "Add school"}</h2>
            <p className="mt-1 text-sm text-[#64748B]">Maintain delivery locations and operational contacts.</p>
          </div>
          {editingSchool ? <button type="button" className="text-sm font-medium text-[#163D6B]" onClick={resetForm}>Cancel</button> : null}
        </div>
        <div className="mt-6 grid gap-4">
          <div>
            <label className="text-sm font-medium text-[#0F172A]">School name</label>
            <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Address</label>
            <input value={form.address} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Contact name</label>
            <input value={form.contact_name} onChange={(event) => setForm((current) => ({ ...current, contact_name: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Phone</label>
              <input value={form.contact_phone} onChange={(event) => setForm((current) => ({ ...current, contact_phone: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Email</label>
              <input value={form.contact_email} onChange={(event) => setForm((current) => ({ ...current, contact_email: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
          </div>
        </div>
        <button type="submit" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white">
          {editingSchool ? <Save size={16} /> : <PlusCircle size={16} />}
          {editingSchool ? "Save changes" : "Add school"}
        </button>
      </form>

      <div className="guardian-card overflow-hidden bg-white">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Schools</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
              <tr>
                <th className="px-6 py-3 font-medium">School</th>
                <th className="px-6 py-3 font-medium">Contact</th>
                <th className="px-6 py-3 font-medium">Pupils</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {schools.length ? (
                schools.map((school) => (
                  <tr key={school.id} className="border-t border-[#E2E8F0]">
                    <td className="px-6 py-4">
                      <p className="font-medium text-[#0F172A]">{school.name}</p>
                      <p className="mt-1 text-xs text-[#64748B]">{school.address}</p>
                    </td>
                    <td className="px-6 py-4 text-[#475569]">
                      <p>{school.contact_name}</p>
                      <p className="text-xs text-[#64748B]">{school.contact_email}</p>
                    </td>
                    <td className="px-6 py-4 text-[#475569]">{school.child_count || 0}</td>
                    <td className="px-6 py-4 text-[#475569]">{formatDateTime(school.created_at)}</td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleEdit(school)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">Edit</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-[#64748B]">No schools configured yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
